import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import emailjs from '@emailjs/browser';
import '../styles/skripte.css';

const Skripte = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
  const [showThankYouModal, setShowThankYouModal] = useState(false);

  // Initialize EmailJS
  useEffect(() => {
    emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);
  }, []);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    const templateParams = {
      subscriber_email: email,
      timestamp: new Date().toLocaleString('hr-HR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    };

    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_SUBSCRIPTION_TEMPLATE_ID,
        templateParams
      );
      
      setSubmitStatus('success');
      setShowThankYouModal(true);
      setEmail('');
    } catch (error) {
      console.error('Email subscription failed:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Skripte Računovodstvo | Skripte Ekonomija | Konto Zona - Strukturirani Materijali</title>
        <meta name="description" content="Skripte iz računovodstva, ekonomije i matematike za studente i srednjoškolce. Strukturirani materijali s primjerima, bilancama, knjiženjima i PDV‑om. Prijavite se za nove skripte." />
        <meta name="keywords" content="skripte računovodstvo, skripte ekonomija, skripte matematika, skripte za studente, skripte za srednjoškolce, materijali za učenje računovodstva, skripte EFZG, skripte EFRI, skripte EFST, skripte EFOS, skripte Zagreb" />
        <meta property="og:title" content="Skripte Računovodstvo | Skripte Ekonomija | Konto Zona" />
        <meta property="og:description" content="Skripte računovodstvo, skripte ekonomija i skripte matematika. Strukturirani materijali s primjerima i detaljnim objašnjenjima za studente i srednjoškolce." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://instrukcijekontozona.hr/skripte" />
        <meta property="og:image" content="https://instrukcijekontozona.hr/kontozona-logo.png" />
        <meta property="og:locale" content="hr_HR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Skripte Računovodstvo | Skripte Ekonomija | Konto Zona" />
        <meta name="twitter:description" content="Skripte računovodstvo, skripte ekonomija i skripte matematika. Strukturirani materijali s primjerima i detaljnim objašnjenjima za studente i srednjoškolce." />
        <meta name="twitter:image" content="https://instrukcijekontozona.hr/kontozona-logo.png" />
        <link rel="canonical" href="https://instrukcijekontozona.hr/skripte" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Skripte Računovodstvo i Ekonomija",
            "description": "Strukturirani materijali iz računovodstva, ekonomije i matematike za studente i srednjoškolce",
            "brand": {
              "@type": "Brand",
              "name": "Konto Zona"
            },
            "offers": {
              "@type": "Offer",
              "availability": "https://schema.org/PreOrder",
              "description": "Skripte će biti dostupne uskoro"
            }
          })}
        </script>
      </Helmet>
      {/* SEKCIJA 2: PONUDA SKRIPTI */}
      <section className="section scripts-offer-executive skripte-section">
        <div className="container">
          <div className="section-title">
            <h1>Skripte Računovodstvo i Ekonomija</h1>
            <p>Svaka skripta je pažljivo strukturiran sustav, dizajniran da ti pruži maksimalno znanje uz minimalan utrošak vremena.</p>
          </div>
          
          <div className="scripts-container-executive">
            {/* SKRIPTA 1: Za Studente */}
            <div className="script-card-executive student-card">
              <div className="script-header-executive">
                <div className="icon-wrapper-executive blue-bg">
                  <i className="fas fa-university"></i>
                </div>
                <div>
                  <span className="badge-executive">ZA STUDENTE</span>
                  <h3>Osnove Računovodstva</h3>
                </div>
              </div>
              <div className="included-content-executive">
                <h4>Što je unutra:</h4>
                <ul>
                  <li><i className="fas fa-check"></i> Sastavljanje i analiza bilance</li>
                  <li><i className="fas fa-check"></i> Račun dobiti i gubitka (RDG)</li>
                  <li><i className="fas fa-check"></i> Knjiženje poslovnih promjena</li>
                  <li><i className="fas fa-check"></i> Primjeri obračuna PDV-a</li>
                </ul>
              </div>
              <div className="script-footer-executive">
                <button className="btn btn-standard disabled" disabled>
                  Dolazi uskoro
                </button>
              </div>
            </div>

            {/* SKRIPTA 2: Za Srednjoškolce */}
            <div className="script-card-executive school-card">
              <div className="script-header-executive">
                <div className="icon-wrapper-executive green-bg">
                  <i className="fas fa-school"></i>
                </div>
                <div>
                  <span className="badge-executive">ZA SREDNJOŠKOLCE</span>
                  <h3>Osnove Računovodstva</h3>
                </div>
              </div>
              <div className="included-content-executive">
                <h4>Što je unutra:</h4>
                <ul>
                  <li><i className="fas fa-check"></i> Temelji bilance i T-konta</li>
                  <li><i className="fas fa-check"></i> Jednostavni RDG primjeri</li>
                  <li><i className="fas fa-check"></i> Osnovna knjiženja za testove</li>
                  <li><i className="fas fa-check"></i> Obračun PDV-a za početnike</li>
                </ul>
              </div>
              <div className="script-footer-executive">
                <button className="btn btn-express disabled" disabled>
                  Dolazi uskoro
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEKCIJA 3: USKORO */}
      <section className="section upcoming-executive skripte-section">
        <div className="container text-center">
          <h2 style={{color: '#000000'}}>Arhiva znanja raste...</h2>
          <p style={{color: '#000000'}}>Materijali će biti dostupni uskoro.</p>
          <p style={{color: '#666666', marginTop: '20px', fontStyle: 'italic'}}>
             Pratite našu stranicu za najnovije informacije.
          </p>
          
          {/* Email Subscription Form */}
          <div className="email-subscription-container">
            <form onSubmit={handleEmailSubmit} className="email-subscription-form">
              <div className="subscription-input-group">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Unesite svoju email adresu"
                  required
                  className="subscription-input"
                />
                <button 
                  type="submit" 
                  className={`subscription-btn ${isSubmitting ? 'loading' : ''}`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Slanje...' : 'Prijavi se'}
                </button>
              </div>
              
              {submitStatus === 'error' && (
                <div className="subscription-error">
                  <i className="fas fa-exclamation-circle"></i>
                  <p>Došlo je do greške pri prijavi. Molimo pokušajte ponovno.</p>
                </div>
              )}
              
            </form>
          </div>
        </div>
      </section>

      {/* Modal zahvale za newsletter */}
      {showThankYouModal && (
        <div className="thank-you-modal-overlay" onClick={() => setShowThankYouModal(false)}>
          <div className="thank-you-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <button
                className="modal-close-btn"
                onClick={() => setShowThankYouModal(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-content">
              <div className="success-icon">
                <i className="fas fa-check-circle"></i>
              </div>

              <h3>Hvala vam!</h3>
              <p>Uspješno ste se prijavili na naš newsletter. Obavijestit ćemo vas čim budu dostupni novi materijali i skripte.</p>

              <button
                className="modal-close-button"
                onClick={() => setShowThankYouModal(false)}
              >
                Zatvori
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Skripte; 